/**
 * Plan Limits Configuration
 * Phase 10: Monetization
 *
 * Defines the features and limits for each subscription plan.
 * -1 means unlimited
 */

import type { PlanId, PlanFeatures, PlanDisplayInfo } from '@/types/billing.types';

/**
 * Default features for the free plan
 */
export const FREE_PLAN_FEATURES: PlanFeatures = {
  max_restaurants: 1,
  max_users: 1,
  max_products: 50,
  max_zones: 3,
  history_days: 7,
  voice_input: true,
  offline_mode: true,
  dlc_alerts: false,
  exports_pdf: false,
  exports_excel: false,
  ai_suggestions: false,
  ai_requests_monthly: 0,
  email_notifications: false,
  recipes: false,
  import_csv: false,
  api_access: false,
  sso: false,
  support_email: false,
  support_chat: false,
  support_phone: false,
};

/**
 * Features for the Starter plan
 */
export const STARTER_PLAN_FEATURES: PlanFeatures = {
  max_restaurants: 1,
  max_users: 3,
  max_products: 300,
  max_zones: 6,
  history_days: 365,
  voice_input: true,
  offline_mode: true,
  dlc_alerts: true,
  exports_pdf: true,
  exports_excel: false,
  ai_suggestions: false,
  ai_requests_monthly: 0,
  email_notifications: false,
  recipes: false,
  import_csv: false,
  api_access: false,
  sso: false,
  support_email: true,
  support_chat: false,
  support_phone: false,
};

/**
 * Features for the Pro plan
 */
export const PRO_PLAN_FEATURES: PlanFeatures = {
  max_restaurants: 3,
  max_users: 10,
  max_products: -1, // unlimited
  max_zones: -1, // unlimited
  history_days: -1, // unlimited
  voice_input: true,
  offline_mode: true,
  dlc_alerts: true,
  exports_pdf: true,
  exports_excel: true,
  ai_suggestions: true,
  ai_requests_monthly: 100,
  email_notifications: true,
  recipes: true,
  import_csv: true,
  api_access: false,
  sso: false,
  support_email: true,
  support_chat: true,
  support_phone: false,
};

/**
 * Features for the Enterprise plan
 */
export const ENTERPRISE_PLAN_FEATURES: PlanFeatures = {
  max_restaurants: -1, // unlimited
  max_users: -1, // unlimited
  max_products: -1, // unlimited
  max_zones: -1, // unlimited
  history_days: -1, // unlimited
  voice_input: true,
  offline_mode: true,
  dlc_alerts: true,
  exports_pdf: true,
  exports_excel: true,
  ai_suggestions: true,
  ai_requests_monthly: -1, // unlimited
  email_notifications: true,
  recipes: true,
  import_csv: true,
  api_access: true,
  sso: true,
  support_email: true,
  support_chat: true,
  support_phone: true,
};

/**
 * Mapping of plan IDs to their features
 */
export const PLAN_LIMITS: Record<PlanId, PlanFeatures> = {
  free: FREE_PLAN_FEATURES,
  starter: STARTER_PLAN_FEATURES,
  pro: PRO_PLAN_FEATURES,
  enterprise: ENTERPRISE_PLAN_FEATURES,
};

/**
 * Plan pricing information
 */
export const PLAN_PRICES: Record<PlanId, { monthly: number; yearly: number }> = {
  free: { monthly: 0, yearly: 0 },
  starter: { monthly: 29, yearly: 290 },
  pro: { monthly: 59, yearly: 590 },
  enterprise: { monthly: 0, yearly: 0 }, // Custom pricing
};

/**
 * Plan display information for pricing page
 */
export const PLANS_DISPLAY: PlanDisplayInfo[] = [
  {
    id: 'free',
    name: 'Gratuit',
    description: 'Pour decouvrir StockChef',
    priceMonthly: 0,
    priceYearly: 0,
    features: [
      { name: '1 restaurant', included: true },
      { name: '1 utilisateur', included: true },
      { name: '50 produits', included: true },
      { name: 'Saisie vocale', included: true },
      { name: 'Mode offline', included: true },
      { name: 'Alertes DLC', included: false },
      { name: 'Export PDF', included: false },
      { name: 'IA suggestions', included: false },
    ],
    cta: 'Commencer gratuitement',
    popular: false,
  },
  {
    id: 'starter',
    name: 'Starter',
    description: 'Pour les restaurants independants',
    priceMonthly: 29,
    priceYearly: 290,
    features: [
      { name: '1 restaurant', included: true },
      { name: '3 utilisateurs', included: true },
      { name: '300 produits', included: true },
      { name: 'Saisie vocale', included: true },
      { name: 'Mode offline', included: true },
      { name: 'Alertes DLC', included: true },
      { name: 'Export PDF', included: true },
      { name: 'IA suggestions', included: false },
    ],
    cta: "Demarrer l'essai",
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Pour les groupes de restaurants',
    priceMonthly: 59,
    priceYearly: 590,
    features: [
      { name: '3 restaurants', included: true },
      { name: '10 utilisateurs', included: true },
      { name: 'Produits illimites', included: true },
      { name: 'Toutes les fonctionnalites', included: true },
      { name: 'IA suggestions', included: true },
      { name: 'Import/Export complet', included: true },
      { name: 'Gestion des recettes', included: true },
      { name: 'Support prioritaire', included: true },
    ],
    cta: 'Passer a Pro',
    popular: true,
  },
];

/**
 * Check if a value represents unlimited
 */
export function isUnlimited(value: number): boolean {
  return value === -1;
}

/**
 * Get features for a plan
 */
export function getPlanFeatures(planId: PlanId): PlanFeatures {
  return PLAN_LIMITS[planId] || FREE_PLAN_FEATURES;
}

/**
 * Get plan price
 */
export function getPlanPrice(
  planId: PlanId,
  cycle: 'monthly' | 'yearly'
): number {
  return PLAN_PRICES[planId]?.[cycle] ?? 0;
}

/**
 * Calculate yearly savings percentage
 */
export function getYearlySavingsPercent(planId: PlanId): number {
  const monthly = PLAN_PRICES[planId]?.monthly ?? 0;
  const yearly = PLAN_PRICES[planId]?.yearly ?? 0;

  if (monthly === 0) return 0;

  const monthlyCostPerYear = monthly * 12;
  const savings = monthlyCostPerYear - yearly;
  return Math.round((savings / monthlyCostPerYear) * 100);
}

/**
 * Feature descriptions for display
 */
export const FEATURE_DESCRIPTIONS: Record<keyof PlanFeatures, string> = {
  max_restaurants: 'Nombre de restaurants',
  max_users: "Nombre d'utilisateurs",
  max_products: 'Nombre de produits',
  max_zones: 'Zones de stockage',
  history_days: "Historique des donnees",
  voice_input: 'Saisie vocale',
  offline_mode: 'Mode hors ligne',
  dlc_alerts: 'Alertes DLC/DLUO',
  exports_pdf: 'Export PDF',
  exports_excel: 'Export Excel/CSV',
  ai_suggestions: 'Suggestions IA',
  ai_requests_monthly: 'Requetes IA par mois',
  email_notifications: 'Notifications email',
  recipes: 'Gestion des recettes',
  import_csv: 'Import CSV/Excel',
  api_access: 'Acces API',
  sso: 'SSO (SAML/OIDC)',
  support_email: 'Support email',
  support_chat: 'Support chat',
  support_phone: 'Support telephonique',
};
